<script setup lang="ts">

import { computed, nextTick, ref, watch } from 'vue'
import { MYSTERY_BOX_TYPE, type MysteryBoxReward } from '@/types/shared/player-config'
import { useConfetti } from '@/composables/game/confetti'
import { MYSTERY_BOX_IMAGES } from '@/config/mystery-box-images'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'

const props = defineProps<{
  reward: MysteryBoxReward[] | null
  boxType: MYSTERY_BOX_TYPE | null
}>()

const emit = defineEmits<{
  (e: 'finish'): void
}>()

const { showConfetti } = useConfetti()
const { addHapticFeedback, addNotificationFeedback } = useHapticFeedback()
const { playSound } = useSounds()

const rewardItem = computed(() => props.reward)
const isOpen = ref(false)

const boxImages = computed(() => {
  if (!props.boxType) return null
  return MYSTERY_BOX_IMAGES[props.boxType]
})

function initAnimation() {
  addHapticFeedback('medium')
  nextTick(() => {
    // Trigger confetti first
    setTimeout(() => {
      playSound(GameSound.MYSTERY_BOX)
      showConfetti({ spread: 220, count: 90 })
      setTimeout(() => playSound(GameSound.BONUS_ACTIVATED), 200)
      addNotificationFeedback('success')
    }, 1500)
    // Open box slightly after confetti starts
    setTimeout(() => isOpen.value = true, 1550)
  })
}

watch([isOpen, rewardItem], ([isOpen, rewardItem]) => {
  if (isOpen && !!rewardItem) {
    setTimeout(() => {
      emit('finish')
    }, 850)
  }
})

// Start animation when component mounts
initAnimation()
</script>

<template>
  <div class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm">
    <Transition name="fade">
      <img
        v-if="(!isOpen || !reward) && boxImages"
        :src="boxImages.closed"
        class="size-[300px] object-contain scale-in box-wiggle"
        alt="Mystery box closed"
      >
    </Transition>
    <Transition name="fade-slow">
      <img
        v-if="isOpen && !!reward && boxImages"
        :src="boxImages.open"
        class="absolute size-[300px] object-contain box-open"
        alt="Mystery box open"
      >
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.fade-slow-enter-active {
  transition: opacity 0.5s ease 0.2s;
}

.fade-slow-leave-active {
  transition: opacity 0.5s ease;
}

.fade-slow-enter-from,
.fade-slow-leave-to {
  opacity: 0;
}

.fade-slow-enter-to,
.fade-slow-leave-from {
  opacity: 1;
}

.box-wiggle {
  animation: wiggle 0.2s ease-in-out infinite;
}

.box-open {
  animation: pop-in 0.3s ease-out;
}

@keyframes wiggle {
  0%, 100% {
    transform: rotate(-2deg);
  }
  50% {
    transform: rotate(2deg);
  }
}

@keyframes pop-in {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
